import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import PageHeader from '../shared/PageHeader';
import { useTheme } from '../../context/ThemeContext';
import {
  generalSettingsApi,
  countriesApi,
  statesApi,
  citiesApi,
  extractOne,
  extractList,
  mailSetupApi,
  analyticsSettingsApi,
  afroNotificationSettingsApi,
} from '../../api/facades';

const inputClass =
  'w-full rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-transparent dark:text-slate-200';

const AFRO_NOTIFICATION_CHANNELS: { value: string; label: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
];

const SettingsPage: React.FC = () => {
  const { pathname } = useLocation();
  const isGeneral = pathname.includes('/settings/general');
  const isSmtp = pathname.includes('/settings/smtp');
  const isAnalytics = pathname.includes('/settings/analytics');
  const isNotifications = pathname.includes('/settings/notifications');
  const queryClient = useQueryClient();
  const { theme, mode, setMode, toggleTheme } = useTheme();

  // General form state
  const [company_name, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [country, setCountry] = useState<number | ''>('');
  const [state, setState] = useState<number | ''>('');
  const [city, setCity] = useState<number | ''>('');
  const [pincode, setPincode] = useState<string>('');
  const [date_formet, setDateFormet] = useState<number | ''>('');
  const [logo_img, setLogoImg] = useState('');
  const [favicon_img, setFaviconImg] = useState('');
  const [timezone, setTimezone] = useState('');

  // SMTP form state
  const [mailHost, setMailHost] = useState('');
  const [mailPort, setMailPort] = useState('');
  const [mailUsername, setMailUsername] = useState('');
  const [mailEmail, setMailEmail] = useState('');
  const [mailPassword, setMailPassword] = useState('');
  const [mailDriver, setMailDriver] = useState('smtp');
  const [mailEncryption, setMailEncryption] = useState('tls');
  const [mailFromName, setMailFromName] = useState('');

  // Analytics integration form state
  const [analyticsBaseUrl, setAnalyticsBaseUrl] = useState('');
  const [analyticsSystemId, setAnalyticsSystemId] = useState('');
  const [analyticsApiKey, setAnalyticsApiKey] = useState('');
  const [analyticsApiKeyPresent, setAnalyticsApiKeyPresent] = useState(false);

  // AfroNotifications (external API: POST …/api/v1/notify, Authorization: Bearer …)
  const [afroBaseUrl, setAfroBaseUrl] = useState('');
  const [afroDefaultChannel, setAfroDefaultChannel] = useState('');
  const [afroBearerToken, setAfroBearerToken] = useState('');
  const [afroBearerPresent, setAfroBearerPresent] = useState(false);
  const [afroTestOpen, setAfroTestOpen] = useState(false);
  const [afroTestRecipient, setAfroTestRecipient] = useState('');
  const [afroTestChannel, setAfroTestChannel] = useState('');

  // Load general settings
  const { data: settingsRes, isLoading: loadingSettings } = useQuery({
    queryKey: ['general-settings'],
    queryFn: () => generalSettingsApi.list(),
  });

  const settings = extractOne(settingsRes);

  useEffect(() => {
    if (!settings || typeof settings !== 'object') return;
    if (settings.company_name != null) setCompanyName(String(settings.company_name));
    if (settings.address != null) setAddress(String(settings.address));
    if (settings.country != null) setCountry(Number(settings.country));
    if (settings.state != null) setState(Number(settings.state));
    if (settings.city != null) setCity(Number(settings.city));
    if (settings.pincode != null) setPincode(String(settings.pincode));
    if (settings.date_formet != null) setDateFormet(Number(settings.date_formet));
    if (settings.logo_img != null) setLogoImg(String(settings.logo_img));
    if (settings.favicon_img != null) setFaviconImg(String(settings.favicon_img));
    if (settings.timezone != null) setTimezone(String(settings.timezone));
  }, [settings]);

  // Cascading reference data
  const { data: countriesRes } = useQuery({
    queryKey: ['countries'],
    queryFn: () => countriesApi.list({ perPage: 200 }),
  });
  const countries = extractList(countriesRes);

  const { data: statesRes } = useQuery({
    queryKey: ['states', country],
    queryFn: () =>
      statesApi.list({ countryId: typeof country === 'number' ? country : undefined, perPage: 200 }),
    enabled: typeof country === 'number',
  });
  const states = extractList(statesRes);

  const { data: citiesRes } = useQuery({
    queryKey: ['cities', state],
    queryFn: () =>
      citiesApi.list({ stateId: typeof state === 'number' ? state : undefined, perPage: 200 }),
    enabled: typeof state === 'number',
  });
  const cities = extractList(citiesRes);

  const onCountryChange = (v: string) => {
    const n = v === '' ? '' : Number(v);
    setCountry(n);
    setState('');
    setCity('');
  };
  const onStateChange = (v: string) => {
    const n = v === '' ? '' : Number(v);
    setState(n);
    setCity('');
  };

  const createOrUpdateMutation = useMutation({
    mutationFn: (body: Parameters<typeof generalSettingsApi.createOrUpdate>[0]) =>
      generalSettingsApi.createOrUpdate(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['general-settings'] });
      toast.success('General settings saved.');
    },
    onError: (err: any) => {
      toast.error(err?.body?.message || err?.message || 'Failed to save settings.');
    },
  });

  const canSubmitGeneral =
    typeof country === 'number' &&
    typeof state === 'number' &&
    typeof city === 'number' &&
    pincode.trim() !== '';

  const onSubmitGeneral = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitGeneral) return;
    createOrUpdateMutation.mutate({
      company_name: company_name || undefined,
      address: address || undefined,
      country: country as number,
      state: state as number,
      city: city as number,
      pincode: parseInt(pincode, 10),
      date_formet: typeof date_formet === 'number' ? date_formet : 1,
      logo_img: logo_img || undefined,
      favicon_img: favicon_img || undefined,
      timezone: timezone || undefined,
    });
  };

  // Load SMTP settings
  const { data: mailRes, isLoading: loadingMail } = useQuery({
    queryKey: ['mail-setup'],
    queryFn: () => mailSetupApi.get(),
  });
  const mailData = extractOne(mailRes);

  useEffect(() => {
    if (!mailData || typeof mailData !== 'object') return;
    if (mailData.mail_host != null) setMailHost(String(mailData.mail_host));
    if (mailData.mail_port != null) setMailPort(String(mailData.mail_port));
    if (mailData.mail_username != null) setMailUsername(String(mailData.mail_username));
    if (mailData.mail_email != null) setMailEmail(String(mailData.mail_email));
    if (mailData.mail_password != null) setMailPassword(String(mailData.mail_password));
    if (mailData.mail_driver != null) setMailDriver(String(mailData.mail_driver));
    if (mailData.mail_encryption != null) setMailEncryption(String(mailData.mail_encryption));
    if (mailData.mail_from_name != null) setMailFromName(String(mailData.mail_from_name));
  }, [mailData]);

  const mailSetupMutation = useMutation({
    mutationFn: () =>
      mailSetupApi.save({
        mail_host: mailHost.trim(),
        mail_port: mailPort.trim(),
        mail_username: mailUsername.trim() || undefined,
        mail_email: mailEmail.trim(),
        mail_password: mailPassword || undefined,
        mail_driver: mailDriver.trim() || undefined,
        mail_encryption: mailEncryption.trim() || undefined,
        mail_from_name: mailFromName.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mail-setup'] });
      toast.success('SMTP settings saved.');
    },
    onError: (err: any) => {
      toast.error(err?.body?.message || err?.message || 'Failed to save SMTP settings.');
    },
  });

  const canSubmitMail = mailHost.trim() !== '' && mailPort.trim() !== '' && mailEmail.trim() !== '';

  // Load analytics settings
  const { data: analyticsRes, isLoading: loadingAnalytics } = useQuery({
    queryKey: ['analytics-settings'],
    queryFn: () => analyticsSettingsApi.get(),
    enabled: isAnalytics,
  });
  const analyticsData = extractOne(analyticsRes);

  useEffect(() => {
    if (!analyticsData || typeof analyticsData !== 'object') return;
    if (analyticsData.base_url != null) setAnalyticsBaseUrl(String(analyticsData.base_url));
    if (analyticsData.system_id != null) setAnalyticsSystemId(String(analyticsData.system_id));
    setAnalyticsApiKeyPresent(Boolean(analyticsData.api_key_present));
    // Never auto-fill API key into browser input.
    setAnalyticsApiKey('');
  }, [analyticsData]);

  const analyticsMutation = useMutation({
    mutationFn: () =>
      analyticsSettingsApi.save({
        base_url: analyticsBaseUrl.trim() || null,
        system_id: analyticsSystemId.trim() || null,
        // send only if user typed something; backend treats blank as "no change"
        api_key: analyticsApiKey.trim() ? analyticsApiKey : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics-settings'] });
      toast.success('Analytics settings saved.');
      setAnalyticsApiKey('');
    },
    onError: (err: any) => {
      toast.error(err?.body?.message || err?.message || 'Failed to save analytics settings.');
    },
  });

  const canSubmitAnalytics = analyticsBaseUrl.trim() !== '' && analyticsSystemId.trim() !== '';

  const { data: afroRes, isLoading: loadingAfro } = useQuery({
    queryKey: ['afro-notification-settings'],
    queryFn: () => afroNotificationSettingsApi.get(),
    enabled: isNotifications,
  });
  const afroData = extractOne(afroRes);

  useEffect(() => {
    if (!afroData || typeof afroData !== 'object') return;
    if (afroData.base_url != null) setAfroBaseUrl(String(afroData.base_url));
    const rawCh =
      afroData.default_channel != null && String(afroData.default_channel).trim() !== ''
        ? String(afroData.default_channel).trim()
        : '';
    setAfroDefaultChannel(
      rawCh && AFRO_NOTIFICATION_CHANNELS.some((o) => o.value === rawCh) ? rawCh : '',
    );
    setAfroBearerPresent(Boolean(afroData.bearer_token_present));
    setAfroBearerToken('');
  }, [afroData]);

  const afroMutation = useMutation({
    mutationFn: () =>
      afroNotificationSettingsApi.save({
        base_url: afroBaseUrl.trim() || null,
        default_channel: afroDefaultChannel.trim() || null,
        bearer_token: afroBearerToken.trim() ? afroBearerToken : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['afro-notification-settings'] });
      toast.success('Notifications settings saved.');
      setAfroBearerToken('');
    },
    onError: (err: any) => {
      toast.error(err?.body?.message || err?.message || 'Failed to save notifications settings.');
    },
  });

  const canSubmitAfro = afroBaseUrl.trim() !== '';

  const afroSavedBaseUrl = (afroData && typeof afroData === 'object' ? String(afroData.base_url ?? '') : '').trim();
  const canUseAfroTest =
    !loadingAfro && afroSavedBaseUrl !== '' && afroBearerPresent;

  const openAfroTestModal = () => {
    setAfroTestRecipient('');
    setAfroTestChannel(afroDefaultChannel || '');
    setAfroTestOpen(true);
  };

  const afroSavedDefaultChannel =
    afroData && typeof afroData === 'object'
      ? String(afroData.default_channel ?? '').trim()
      : '';
  const canSubmitAfroTest =
    afroTestRecipient.trim() !== '' &&
    (afroTestChannel.trim() !== '' || afroSavedDefaultChannel !== '');

  const afroTestMutation = useMutation({
    mutationFn: () =>
      afroNotificationSettingsApi.test({
        recipient: afroTestRecipient.trim(),
        channel: afroTestChannel.trim() || null,
      }),
    onSuccess: (axiosRes) => {
      const msg = axiosRes?.data?.message ?? 'Test notification sent.';
      toast.success(msg);
      setAfroTestOpen(false);
      setAfroTestRecipient('');
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      const msg = data?.message || err?.body?.message || err?.message || 'Test failed.';
      const detail = data?.data?.detail;
      toast.error(detail ? `${msg}: ${detail}` : msg);
    },
  });

  return (
    <div>
      {!isGeneral && !isSmtp && !isAnalytics && !isNotifications && (
        <>
          <PageHeader title="Appearance" subtitle="Theme and display preferences." />
          <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
            <div className="flex items-center justify-between rounded-xl p-4">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Theme</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Mode: {mode} • Effective: {theme}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs text-slate-500">
                  <span className="sr-only">Theme mode</span>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value as typeof mode)}
                    className="rounded-lg bg-gray-50 py-2 px-3 text-sm text-slate-700 focus:outline-none dark:bg-transparent dark:text-slate-200"
                  >
                    <option value="system">System</option>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </select>
                </label>
                <Button variant="primary" onClick={toggleTheme}>
                  Toggle theme
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {isGeneral && (
        <>
          <PageHeader
            title="General"
            subtitle="Company details and address. Country, state, city, pincode and date format are required to save."
          />
          <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
            {loadingSettings ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : (
              <form onSubmit={onSubmitGeneral} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormField label="Company name">
                    <input
                      className={inputClass}
                      value={company_name}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="e.g. ClickLaw"
                    />
                  </FormField>
                </div>
                <div className="md:col-span-2">
                  <FormField label="Address">
                    <input
                      className={inputClass}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Street, building"
                    />
                  </FormField>
                </div>
                <FormField label="Country" hint="Required">
                  <select
                    className={inputClass}
                    value={country === '' ? '' : country}
                    onChange={(e) => onCountryChange(e.target.value)}
                    required
                  >
                    <option value="">Select country</option>
                    {countries.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="State" hint="Required">
                  <select
                    className={inputClass}
                    value={state === '' ? '' : state}
                    onChange={(e) => onStateChange(e.target.value)}
                    required
                    disabled={typeof country !== 'number'}
                  >
                    <option value="">Select state</option>
                    {states.map((s: any) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="City" hint="Required">
                  <select
                    className={inputClass}
                    value={city === '' ? '' : city}
                    onChange={(e) => setCity(e.target.value === '' ? '' : Number(e.target.value))}
                    required
                    disabled={typeof state !== 'number'}
                  >
                    <option value="">Select city</option>
                    {cities.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Pincode" hint="Required">
                  <input
                    className={inputClass}
                    type="text"
                    inputMode="numeric"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="e.g. 123456"
                    required
                  />
                </FormField>
                <FormField label="Date format (number)" hint="Required, e.g. 1">
                  <input
                    className={inputClass}
                    type="number"
                    min={1}
                    value={date_formet === '' ? '' : date_formet}
                    onChange={(e) =>
                      setDateFormet(
                        e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1),
                      )
                    }
                    placeholder="1"
                  />
                </FormField>
                <FormField label="Logo image (path or URL)">
                  <input
                    className={inputClass}
                    value={logo_img}
                    onChange={(e) => setLogoImg(e.target.value)}
                    placeholder="logo.png"
                  />
                </FormField>
                <FormField label="Favicon image (path or URL)">
                  <input
                    className={inputClass}
                    value={favicon_img}
                    onChange={(e) => setFaviconImg(e.target.value)}
                    placeholder="favicon.ico"
                  />
                </FormField>
                <div className="md:col-span-2">
                  <FormField label="Timezone">
                    <input
                      className={inputClass}
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      placeholder="e.g. UTC, America/New_York"
                    />
                  </FormField>
                </div>
                <div className="md:col-span-2 flex items-center justify-end gap-2 mt-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!canSubmitGeneral || createOrUpdateMutation.isPending}
                  >
                    {createOrUpdateMutation.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </>
      )}

      {isSmtp && (
        <>
          <PageHeader
            title="SMTP / Email"
            subtitle="Configure outgoing email server settings. Only administrators should change this."
          />
          <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
            {loadingMail ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!canSubmitMail) return;
                  mailSetupMutation.mutate();
                }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <FormField label="Mail host" hint="Required">
                  <input
                    className={inputClass}
                    value={mailHost}
                    onChange={(e) => setMailHost(e.target.value)}
                    placeholder="smtp.example.com"
                    required
                  />
                </FormField>
                <FormField label="Port" hint="Required">
                  <input
                    className={inputClass}
                    value={mailPort}
                    onChange={(e) => setMailPort(e.target.value)}
                    placeholder="587"
                    required
                  />
                </FormField>
                <FormField label="Username">
                  <input
                    className={inputClass}
                    value={mailUsername}
                    onChange={(e) => setMailUsername(e.target.value)}
                    placeholder="SMTP username"
                  />
                </FormField>
                <FormField label="Password">
                  <input
                    className={inputClass}
                    type="password"
                    value={mailPassword}
                    onChange={(e) => setMailPassword(e.target.value)}
                    placeholder="SMTP password"
                  />
                </FormField>
                <FormField label="From email" hint="Required">
                  <input
                    className={inputClass}
                    type="email"
                    value={mailEmail}
                    onChange={(e) => setMailEmail(e.target.value)}
                    placeholder="noreply@example.com"
                    required
                  />
                </FormField>
                <FormField label="From name">
                  <input
                    className={inputClass}
                    value={mailFromName}
                    onChange={(e) => setMailFromName(e.target.value)}
                    placeholder="ClickLaw"
                  />
                </FormField>
                <FormField label="Driver">
                  <select
                    className={inputClass}
                    value={mailDriver}
                    onChange={(e) => setMailDriver(e.target.value)}
                  >
                    <option value="smtp">SMTP</option>
                    <option value="sendmail">sendmail</option>
                    <option value="log">log</option>
                  </select>
                </FormField>
                <FormField label="Encryption">
                  <select
                    className={inputClass}
                    value={mailEncryption}
                    onChange={(e) => setMailEncryption(e.target.value)}
                  >
                    <option value="">None</option>
                    <option value="tls">TLS</option>
                    <option value="ssl">SSL</option>
                  </select>
                </FormField>
                <div className="md:col-span-2 flex items-center justify-end gap-2 mt-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!canSubmitMail || mailSetupMutation.isPending}
                  >
                    {mailSetupMutation.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </>
      )}

      {isAnalytics && (
        <>
          <PageHeader
            title="Analytics"
            subtitle="Configure server-side forwarding of product analytics events to the external Analytics app."
          />
          <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
            {loadingAnalytics ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!canSubmitAnalytics) return;
                  analyticsMutation.mutate();
                }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <div className="md:col-span-2">
                  <FormField label="Analytics base URL" hint="Required">
                    <input
                      className={inputClass}
                      value={analyticsBaseUrl}
                      onChange={(e) => setAnalyticsBaseUrl(e.target.value)}
                      placeholder="e.g. https://analytics.example.com"
                      required
                    />
                  </FormField>
                </div>
                <FormField label="System ID" hint="Required">
                  <input
                    className={inputClass}
                    value={analyticsSystemId}
                    onChange={(e) => setAnalyticsSystemId(e.target.value)}
                    placeholder="e.g. CLICKLAW"
                    required
                  />
                </FormField>
                <FormField
                  label="API key"
                  hint={analyticsApiKeyPresent ? 'Saved (not shown)' : 'Not set'}
                >
                  <input
                    className={inputClass}
                    type="password"
                    value={analyticsApiKey}
                    onChange={(e) => setAnalyticsApiKey(e.target.value)}
                    placeholder={analyticsApiKeyPresent ? '•••••••• (enter to replace)' : 'Enter API key'}
                    autoComplete="new-password"
                  />
                </FormField>
                <div className="md:col-span-2 flex items-center justify-end gap-2 mt-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!canSubmitAnalytics || analyticsMutation.isPending}
                  >
                    {analyticsMutation.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </>
      )}

      {isNotifications && (
        <>
          <PageHeader
            title="Notifications"
            subtitle="AfroNotifications: base URL and Bearer token for POST /api/v1/notify (channel, recipient, message). Default channel is used when sending from the server later."
          />
          <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
            {loadingAfro ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!canSubmitAfro) return;
                  afroMutation.mutate();
                }}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <div className="md:col-span-2">
                  <FormField
                    label="Notifications base URL"
                    hint="Required — e.g. https://notifications.whattodo.tech (no trailing path)"
                  >
                    <input
                      className={inputClass}
                      value={afroBaseUrl}
                      onChange={(e) => setAfroBaseUrl(e.target.value)}
                      placeholder="https://notifications.whattodo.tech"
                      required
                    />
                  </FormField>
                </div>
                <FormField label="Default channel" hint="Optional — used for server-side notify calls">
                  <select
                    className={inputClass}
                    value={afroDefaultChannel}
                    onChange={(e) => setAfroDefaultChannel(e.target.value)}
                  >
                    <option value="">None</option>
                    {AFRO_NOTIFICATION_CHANNELS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <div className="md:col-span-2">
                  <FormField
                    label="Bearer token (API key)"
                    hint={afroBearerPresent ? 'Saved (not shown)' : 'Not set'}
                  >
                    <input
                      className={inputClass}
                      type="password"
                      value={afroBearerToken}
                      onChange={(e) => setAfroBearerToken(e.target.value)}
                      placeholder={afroBearerPresent ? '•••••••• (enter to replace)' : 'Paste Bearer token'}
                      autoComplete="new-password"
                    />
                  </FormField>
                </div>
                <div className="md:col-span-2 flex flex-col gap-2 mt-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md">
                    Send test uses the saved base URL and bearer token. Save first if you changed them.
                    Errors such as &quot;Connection Closed&quot; come from the notifications provider
                    (often the WhatsApp session or gateway), not from ClickLaw.
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={openAfroTestModal}
                      disabled={!canUseAfroTest}
                    >
                      Send test
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={!canSubmitAfro || afroMutation.isPending}
                    >
                      {afroMutation.isPending ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {afroTestOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => !afroTestMutation.isPending && setAfroTestOpen(false)}
                aria-hidden="true"
              />
              <div
                className="relative z-10 w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-slate-800"
                role="dialog"
                aria-modal="true"
                aria-labelledby="afro-test-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h2
                  id="afro-test-title"
                  className="text-lg font-semibold text-slate-700 dark:text-slate-200"
                >
                  Send test notification
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Message: &quot;ClickLaw test notification&quot;
                </p>
                <div className="mt-4 space-y-4">
                  <FormField label="Destination" hint="Phone, email, or id as required by the channel">
                    <input
                      className={inputClass}
                      value={afroTestRecipient}
                      onChange={(e) => setAfroTestRecipient(e.target.value)}
                      placeholder="e.g. 265995908058"
                      autoFocus
                      autoComplete="off"
                    />
                  </FormField>
                  <FormField
                    label="Channel"
                    hint={
                      afroSavedDefaultChannel
                        ? 'Prefilled with your saved default; pick another or use saved default.'
                        : 'Select a channel (or set a default in settings above).'
                    }
                  >
                    <select
                      className={inputClass}
                      value={afroTestChannel}
                      onChange={(e) => setAfroTestChannel(e.target.value)}
                    >
                      <option value="">
                        {afroSavedDefaultChannel ? 'Use saved default' : 'Select channel'}
                      </option>
                      {AFRO_NOTIFICATION_CHANNELS.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </FormField>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => setAfroTestOpen(false)}
                    disabled={afroTestMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => afroTestMutation.mutate()}
                    disabled={!canSubmitAfroTest || afroTestMutation.isPending}
                  >
                    {afroTestMutation.isPending ? 'Sending…' : 'Send'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SettingsPage;

